import React, { useEffect } from 'react';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import QuickButton from 'Douleutaras/components/QuickButton/QuickButton';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import { Trans } from 'react-i18next';
import Modal from 'Douleutaras/components/Modal/Modal';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { getNeedHelpOptions } from 'Douleutaras/redux/data/ccf/needHelpOptionsSlice';
import {
  CCFModalSlugs,
  FormattedMilestone,
  NeedHelpJobChoiceTreeBid,
  NeedHelpModalOptions,
  NeedHelpModalOptionsPerJob
} from 'Douleutaras/types/index';
import {
  MODAL_BY_MILESTONE_POSITION,
  NeedHelpModalOptionsTranslated
} from 'Douleutaras/constants/ccf/need-help-options';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { CCFModalType } from 'Douleutaras/functional-components/ccf/CCFModals';

interface NeedHelpModalProps {
  isOpen: boolean;
  jobId: string;
  bidId: string;
  type: CCFModalType;
  showBackButton?: boolean;
  currentMilestone?: FormattedMilestone;
  mostProgressedMilestoneStep: number;
  totalBidsCount?: number;
  userMugshot?: string;
  sellerFirstName?: string;
  sellerPreposition?: string;
  title: React.ReactElement;
  onBack?(): void;
  onOptionSelected(options: { modalOptions: {}; modalSlug: CCFModalSlugs, optionSlug: NeedHelpModalOptions }): void;
  setIsOpen(modalStatus): void;
}

const selectProperNeedHelpOptionsObject = (needHelpOptions: NeedHelpJobChoiceTreeBid, totalBidsCount: number,
                                           mostProgressedMilestoneStep: number): NeedHelpModalOptions => {

  const mostProgressedMilestone = MODAL_BY_MILESTONE_POSITION[mostProgressedMilestoneStep];

  switch (true) {
    case totalBidsCount === 1:
      return needHelpOptions[NeedHelpModalOptionsPerJob.job_with_one_bid]?.[mostProgressedMilestone];
    case totalBidsCount >= 1:
      return needHelpOptions[NeedHelpModalOptionsPerJob.job_with_multiple_bids]?.[mostProgressedMilestone];
    default:
      return;
  }
};

export const NeedHelpModal = (props: NeedHelpModalProps) => {
  const {
    isOpen,
    setIsOpen,
    jobId,
    bidId,
    currentMilestone,
    onOptionSelected,
    type = CCFModalType.BID,
    totalBidsCount,
    mostProgressedMilestoneStep,
    showBackButton = false,
    onBack,
    userMugshot,
    title
  } = props;
  const isJobType = type === CCFModalType.JOB;
  const dispatch = useAppDispatch();
  const currentMilestonePosition = currentMilestone?.position;
  const needHelpOptions =
    useAppSelector(state => state.needHelpOptions[type] || {});

  useEffect(() => {
    if (isOpen && Object.keys(needHelpOptions).length === 0) {
      dispatch(getNeedHelpOptions({ jobId, bidId }));
    }
  }, [ isOpen ]);

  const job = useAppSelector(state => jobSelectors.selectById(state, jobId));

  // Choose whether the options are based on a "bid" or on an entire "job"
  const needHelpOptionsParsed = isJobType ?
    selectProperNeedHelpOptionsObject(needHelpOptions as NeedHelpJobChoiceTreeBid, totalBidsCount, mostProgressedMilestoneStep)
    : needHelpOptions[MODAL_BY_MILESTONE_POSITION[currentMilestonePosition]];
  const user = useAppSelector(selectCurrentUser);

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>
      <DefaultModal
        serviceIcon={PopupServiceImg}
        serviceIconAlt={<Trans>T.CCF.NEED HELP IMAGE ALT TEXT</Trans>}
        title={title}
        backText={<Trans>T.CCF.NEED HELP BACK BUTTON TEXT</Trans>}
        backTextAriaLabel={<Trans>T.CCF.NEED HELP BACK BUTTON TEXT</Trans>}
        closeAriaLabel={<Trans>T.CCF.NEED HELP CLOSE ICON LABEL</Trans>}
        closeButtonCallback={() => setIsOpen(false)}
        backButtonCallback={onBack}
        showBackButton={showBackButton}
        userMugshot={userMugshot}
      >
        <div>
          { Boolean(needHelpOptionsParsed) && Object.keys(needHelpOptionsParsed)
            .filter(optionKey => needHelpOptionsParsed[optionKey].active && NeedHelpModalOptionsTranslated[optionKey] !== undefined)
            .map((optionKey: NeedHelpModalOptions) => {
              if (optionKey === 'i_want_to_correct_my_job_status' && !user.status_update_enabled) {
                // tslint:disable-next-line:no-null-keyword
                return null;
              }

              // Do not show "Update status" option for a credit model
              if (job.attributes.pricingModel === 'credits_model' && optionKey === 'i_want_to_correct_my_job_status') {
                // tslint:disable-next-line:no-null-keyword
                return null;
              }

              return (<React.Fragment key={`need_help_modal_${optionKey}`}>
                <ModalSelectionItem>
                  <QuickButton
                    title={NeedHelpModalOptionsTranslated[optionKey]}
                    onClick={() => onOptionSelected({
                      optionSlug: optionKey,
                      modalSlug: needHelpOptionsParsed[optionKey]?.modal,
                      modalOptions: needHelpOptionsParsed[optionKey]?.modal_options
                    })}
                  />
                </ModalSelectionItem>
              </React.Fragment>);
            })
          }
        </div>
      </DefaultModal>
    </Modal>
  );
};

export default NeedHelpModal;
